import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import { useTransition, animated } from 'react-spring';
import AppToolbar, { AppToolbarProps } from 'components/AppToolbar';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import Styled from './styles';

const DEFAULT_ANIMATION = (isFloating, immediate) => ({
  from: { transform: 'translateY(-100%)', opacity: 0 },
  enter: { transform: 'translateY(0%)', opacity: 1 },
  leave: { transform: 'translateY(-100%)', opacity: 0 },
  config: {
    duration: isFloating ? getDuration(TOKENS.SPEED.SPEED_FAST) : getDuration(TOKENS.SPEED.SPEED_SLOW),
    easing: getEasing(TOKENS.TRANSITION.TRANSITION_DECELERATE)
  },
  immediate,
});

export interface MultiSelectToolbarProps extends React.ComponentPropsWithRef<'div'> {
  /** Place MultiSelectToolbar content here, eg. <AppToolbarTitle /> */
  children: string | React.ReactNode;
  /** Toggle showing the toolbar */
  show?: boolean;
  /** Toggle between visual variants of Card.
   *  'floating' - places the MultiSelectToolbar above an AppToolbar (useful if there is an AppToolbar already in place).
   */
  variant?: 'default' | 'floating';
  /** Configure the animation. Uses 'react-spring' see https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Forward props to AppToolbar, see AppToolbar for more information on available props. */
  appToolbarProps?: Partial<AppToolbarProps>;
  /** @ignore */
  style?: React.CSSProperties
}

/** MultiSelect Toolbar is a toolbar that displays collective actions for selected items. For use in DataGrid and Card. */
const MultiSelectToolbar = React.forwardRef<HTMLElement, MultiSelectToolbarProps>((props: MultiSelectToolbarProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const {
      children, variant, show, style, animationProps, appToolbarProps, disableAnimation: propsdisableAnimation, ...otherProps
    } = props;
    const isFloating = variant === 'floating';
    const transitions = useTransition(show, null, animationProps || DEFAULT_ANIMATION(isFloating, isAnimationDisabled(propsdisableAnimation, disableAnimation)));
    return transitions.map(({ item, key, props: transitionStyles }) =>
      item && (
      <Styled.MultiSelectToolbar
        as={animated.div}
        key={key}
        style={{ ...transitionStyles, ...style }}
        ref={ref}
        $RTL={RTL}
        $variant={variant}
        {...otherProps}
      >
        <AppToolbar {...appToolbarProps}>{children}</AppToolbar>
      </Styled.MultiSelectToolbar>
      ));
  }));

MultiSelectToolbar.displayName = 'MultiSelectToolbar';

MultiSelectToolbar.defaultProps = {
  show: false,
  variant: 'default',
  animationProps: undefined,
  disableAnimation: undefined,
  appToolbarProps: {},
  style: {}
};

export { MultiSelectToolbar as default };
